import React from 'react'
import { Flame, BookOpen } from 'lucide-react'
import { useEffect, useState } from 'react'
import { dashboardAPI } from '@renderer/api/dashboard'

const STAT_CARD =
  'flex-1 flex items-center gap-3 rounded-2xl px-4 py-3.5'
const STAT_CARD_STYLE = {
  background: '#ffffff',
  border: '1px solid #e0d4be',
  boxShadow: '0 4px 24px rgba(0,0,0,0.07)'
}

const RingCardTall: React.FC<{
  percent: number
  color: string
  display: React.ReactNode
  title: string
  subtitle: string
}> = ({ percent, color, display, title, subtitle }) => (
  <div
    className="h-full w-full flex flex-col items-center justify-center gap-3 rounded-2xl px-4 py-4 text-center"
    style={STAT_CARD_STYLE}
  >
    <div
      className="grid shrink-0 place-items-center rounded-full"
      style={{ width: 'clamp(64px, 9vw, 84px)', height: 'clamp(64px, 9vw, 84px)', background: `conic-gradient(${color} ${percent * 3.6}deg, #e8e1cf 0deg)` }}
    >
      <div className="grid place-items-center rounded-full bg-white text-base font-bold text-[#181820]" style={{ width: '76%', height: '76%' }}>
        {display}
      </div>
    </div>
    <div>
      <p className="text-xs font-semibold text-[#181820]">{title}</p>
      <p className="text-[11px] text-[#5A5A6D] mt-0.5">{subtitle}</p>
    </div>
  </div>
)

const IconRow: React.FC<{
  icon: React.ReactNode
  value: React.ReactNode
  title: string
  subtitle: string
}> = ({ icon, value, title, subtitle }) => (
  <div className={STAT_CARD} style={STAT_CARD_STYLE}>
    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#f5ede0]">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-base font-bold text-[#181820] leading-tight">{value}</p>
      <p className="text-[11px] font-semibold text-[#5A5A6D] mt-0.5">{title}</p>
      <p className="text-[11px] text-[#5A5A6D]/80 mt-0.5">{subtitle}</p>
    </div>
  </div>
)

export const LearningProgress: React.FC = () => {
  const [dayStreak, setDayStreak] = useState(0)
  const [progress, setProgress] = useState(0)
  const [completedModules, setCompletedModules] = useState(0)
  const [totalModules, setTotalModules] = useState(0)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [streakData, progressData] = await Promise.all([
          dashboardAPI.getDayStreak(),
          dashboardAPI.getLabProgressSummary()
        ])
        setDayStreak(streakData.day_streak)
        setProgress(progressData.percentage)
        setCompletedModules(progressData.completed_modules)
        setTotalModules(progressData.total_modules)
      } catch {
        // ignore
      }
    }
    fetchData()
  }, [])

  return (
    <div className="flex h-full gap-3">
      <div className="w-1/2 flex flex-col gap-3">
        <IconRow
          icon={<BookOpen className="w-4 h-4 text-[#7a6020]" />}
          value={
            <>
              {completedModules}
              <span className="text-xs font-medium text-[#5A5A6D]"> / {totalModules}</span>
            </>
          }
          title="MODULES COMPLETED"
          subtitle="Across all enrolled courses"
        />
        <IconRow
          icon={<Flame className="w-4 h-4 text-[#F97316]" />}
          value={dayStreak}
          title="DAY STREAK"
          subtitle={dayStreak === 1 ? '1 day in a row' : `${dayStreak} days in a row`}
        />
      </div>
      <div className="w-1/2">
        <RingCardTall
          percent={progress}
          color="#7a6020"
          display={`${progress}%`}
          title="Lab Progress"
          subtitle="Overall completion"
        />
      </div>
    </div>
  )
}
